import { ReplenishmentSiteSku } from 'Features/Projects/Scenarios/EditSettings/NetworkSettings/Replenishment/Replenishment.types'
import { toast } from 'react-hot-toast'
import { useMemo } from 'react'
import { GridColDef } from '@mui/x-data-grid-premium'
import { Box } from '@mui/material'
import { BaseTable } from 'Features/Projects/Table/BaseTable.view'
import { useSettingsContext } from 'Features/Projects/Scenarios/EditSettings/EditSettings.types'
import {
  useUpdateReplenishmentSiteSkus,
  useUploadReplenishmentSiteSkus,
} from './Replenishment.data'

//
// {
//   1: {baseSourceSiteCode: "WD", cycleTime: 8, preferredTemplate: "constrained", workcentreCode: "WDL3"}
// }

interface ReplenishmentCycleTimeDataInterface {
  [key: number]: {
    baseSourceSiteCode: string
    cycleTime: number
    preferredTemplate: string
    workcentreCode: string
  }
}
interface ReplenishmentTableProps {
  data: ReplenishmentSiteSku[]
  isLoading: boolean
  replenishmentCycleTimeData: ReplenishmentCycleTimeDataInterface
}
export const ReplenishmentTable = ({
  data,
  isLoading,
  replenishmentCycleTimeData,
}: ReplenishmentTableProps) => {
  const onSuccess = () => {
    toast.success('Update Successful')
  }
  const onError = () => {
    toast.error('Update Failed')
  }

  const { state } = useSettingsContext()
  const { projectId, scenarioId } = state
  const { mutate, isPending: isSaving } = useUpdateReplenishmentSiteSkus(
    onSuccess,
    onError
  )
  const onUploadSuccess = () => {
    toast.success('Upload Successful')
  }
  const onUploadError = () => {
    toast.error('Upload Failed')
  }
  const { mutate: uploadMutate, isPending: isUploading } =
    useUploadReplenishmentSiteSkus(onUploadSuccess, onUploadError)

  const updateTableData = (
    changedRows: Record<string, ReplenishmentSiteSku>
  ) => {
    mutate({
      changedRows,
      projectId,
      scenarioId,
    })
  }

  const COLUMN_WIDTH = 150
  const columns: GridColDef[] = useMemo(() => {
    return [
      {
        field: 'baseSourceSiteCode',
        headerName: 'Base Source Site Code',
        width: COLUMN_WIDTH,
        type: 'string',
        pinned: 'left',
      },
      {
        field: 'destinationSiteCode',
        headerName: 'Destination Site Code',
        width: COLUMN_WIDTH,
        type: 'string',
        pinned: 'left',
      },
      {
        field: 'skuGroupCode',
        headerName: 'Sku Group Code',
        width: COLUMN_WIDTH,
        type: 'string',
        pinned: 'left',
      },
      {
        field: 'primaryWorkCentreCode',
        headerName: 'Primary Work Centre Code',
        width: COLUMN_WIDTH,
        type: 'string',
      },
      {
        field: 'planningPeriod',
        headerName: 'Planning Period',
        width: COLUMN_WIDTH,
        type: 'number',
        valueFormatter: (value) => {
          if (value === null || value === undefined) return ''
          return value
        },
      },
      {
        field: 'supplyTypeCode',
        headerName: 'Supply Type Code',
        width: COLUMN_WIDTH,
        type: 'singleSelect',
        editable: true,
        valueOptions: ['COUPLED', 'DECOUPLED'],
      },
      {
        field: 'replenishmentCycleTime',
        headerName: 'Replenishment Cycle Time',
        width: COLUMN_WIDTH,
        type: 'number',
        editable: true,
        valueOptions: [1, 2, 4, 8],
        valueGetter: (value, params) => {
          // if COUPLED --> PrimaryTemplateCycleTime if DECOUPLED --> ReplenishmentCycleTime
          if (params.supplyTypeCode == 'COUPLED') {
            const { skuGroupKey } = params
            if (
              Object.prototype.hasOwnProperty.call(
                replenishmentCycleTimeData,
                skuGroupKey
              )
            ) {
              return replenishmentCycleTimeData[skuGroupKey]?.cycleTime
            }
          }
          return value
        },
      },
      {
        field: 'replenishmentTypeCode',
        headerName: 'Replenishment Type Code',
        width: COLUMN_WIDTH,
        type: 'string',
      },
      {
        field: 'sourceSiteCode',
        headerName: 'Source Site Code',
        width: COLUMN_WIDTH,
        type: 'string',
      },
      {
        field: 'deploymentLeadTime',
        headerName: 'Deployment Lead Time',
        width: COLUMN_WIDTH,
        type: 'number',
        editable: true,
        valueFormatter: (value) => {
          if (value === null || value === undefined) return ''
          return value
        },
      },
      {
        field: 'minimumOrderQuantity',
        headerName: 'Minimum Order Quantity',
        width: COLUMN_WIDTH,
        type: 'number',
        editable: true,
        valueFormatter: (value) => {
          if (value === null || value === undefined) return ''
          return value
        },
      },
      {
        field: 'minimumOrderIncrement',
        headerName: 'Minimum Order Increment',
        width: COLUMN_WIDTH,
        type: 'number',
        valueFormatter: (value) => {
          if (value === null || value === undefined) return ''
          return value
        },
      },
      {
        field: 'segment',
        headerName: 'Segment',
        width: COLUMN_WIDTH,
        type: 'string',
      },
      {
        field: 'serviceLevel',
        headerName: 'Service Level',
        width: COLUMN_WIDTH,
        type: 'number',
        valueFormatter: (value) => {
          if (value === null || value === undefined) return ''
          return `${(value * 100).toFixed(0)}%`
        },
      },
      // Commented out hidden columns
      // {
      //   field: 'workCentreCode',
      //   headerName: 'Work Centre Code',
      //   width: COLUMN_WIDTH,
      //   type: 'string',
      //   hide: true,
      // },
      // {
      //   field: 'supplyTypeKey',
      //   headerName: 'Supply Type Key',
      //   width: COLUMN_WIDTH,
      //   type: 'number',
      //   hide: true,
      // },
      // {
      //   field: 'replenishmentTypeKey',
      //   headerName: 'Replenishment Type Key',
      //   width: COLUMN_WIDTH,
      //   type: 'number',
      //   hide: true,
      // },
      // {
      //   field: 'workCentreKey',
      //   headerName: 'Work Centre Key',
      //   width: COLUMN_WIDTH,
      //   type: 'number',
      //   hide: true,
      // },
      // {
      //   field: 'baseSourceSiteKey',
      //   headerName: 'Base Source Site Key',
      //   width: COLUMN_WIDTH,
      //   type: 'number',
      //   hide: true,
      // },
      // {
      //   field: 'destinationSiteKey',
      //   headerName: 'Destination Site Key',
      //   width: COLUMN_WIDTH,
      //   type: 'number',
      //   hide: true,
      // },
      // {
      //   field: 'id',
      //   headerName: 'Id',
      //   width: COLUMN_WIDTH,
      //   type: 'number',
      //   hide: true,
      // },
      // {
      //   field: 'skuGroupKey',
      //   headerName: 'Sku Group Key',
      //   width: COLUMN_WIDTH,
      //   type: 'number',
      //   hide: true,
      // },
      // {
      //   field: 'sourceSiteKey',
      //   headerName: 'Source Site Key',
      //   width: COLUMN_WIDTH,
      //   type: 'number',
      //   hide: true,
      // },
    ]
  }, [replenishmentCycleTimeData])

  // Process row updates for the replenishment data
  const processRow = (newRow: ReplenishmentSiteSku) => {
    if (newRow.supplyTypeCode == 'COUPLED') {
      const { skuGroupKey } = newRow
      if (
        Object.prototype.hasOwnProperty.call(
          replenishmentCycleTimeData,
          skuGroupKey
        )
      ) {
        newRow.replenishmentCycleTime =
          replenishmentCycleTimeData[Number(skuGroupKey)]?.cycleTime
      } else {
        newRow.supplyTypeCode = 'DECOUPLED'
      }
    }
    return newRow
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flex: 1,
        overflow: 'auto',
      }}
    >
      <BaseTable
        data={data}
        columns={columns}
        isLoading={isLoading ?? false}
        updateTableData={updateTableData}
        uploadData={(file: File) => {
          uploadMutate({ file, projectId, scenarioId })
        }}
        isUploading={isUploading ?? false}
        customDownloadDataMethod={(tableData) => tableData}
        isSaving={isSaving ?? false}
        checkBoxSelection={true}
        processRow={processRow}
        pinnedColumns={[
          'baseSourceSiteCode',
          'destinationSiteCode',
          'skuGroupCode',
        ]}
      />
    </Box>
  )
}
